import { Flex, Table, TableColumnsType } from 'antd'
import { SelectProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'

import { useInstitutionServiceGetDeviceListEndpoint } from 'src/api/queries'
import { DeviceSortBy } from 'src/api/requests'
import { SearchBar, Select } from 'src/components'
import { useDefaultSortingItem, useTablePagination } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { getOrderingDirection, normalizeOrdering } from 'src/utils'

type Props = {
  id?: string
}

export const Equipment = ({ id }: Props) => {
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useState('')
  const { t, currentLang } = useLocales()
  const { defaultSortingOption } = useDefaultSortingItem()

  const pagination = useTablePagination()

  const orderingOptions: SelectProps['options'] = [
    defaultSortingOption,
    {
      value: DeviceSortBy.CODE,
      label: t('organizational_structure.equipment_tab.sort_code_asc')
    },
    {
      value: `-${DeviceSortBy.CODE}`,
      label: t('organizational_structure.equipment_tab.sort_code_desc')
    },
    {
      value:
        currentLang?.value === 'ro'
          ? DeviceSortBy.NAME_RO
          : DeviceSortBy.NAME_RU,
      label: t('organizational_structure.equipment_tab.sort_name_asc')
    },
    {
      value:
        currentLang?.value === 'ro'
          ? `-${DeviceSortBy.NAME_RO}`
          : `-${DeviceSortBy.NAME_RU}`,
      label: t('organizational_structure.equipment_tab.sort_name_desc')
    }
  ]

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    institutionsIds: [String(id)],
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!ordering && {
      sortBy: normalizeOrdering(ordering) as DeviceSortBy,
      sortDirection: getOrderingDirection(ordering)
    })
  }

  const { data, isLoading } =
    useInstitutionServiceGetDeviceListEndpoint(queryParams)

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'code',
        width: '20%',
        title: t('organizational_structure.equipment_tab.unique_number'),
        dataIndex: 'code'
      },
      {
        key: 'name',
        title: t('organizational_structure.equipment_tab.name'),
        render: (_, { name }) => name?.[currentLang?.value]
      },
      {
        key: 'name',
        title: t('common.type'),
        render: (_, { type }) => type?.name?.[currentLang.value]
      },
      {
        key: 'last inspection date',
        title: t('equipments.last_inspection_date'),
        render: (_, { lastInspectionDate }) =>
          lastInspectionDate
            ? dayjs
                .utc(lastInspectionDate)
                .tz()
                .format(dayMonthYearFormatWithDot)
            : '-'
      }
    ],
    [t]
  )

  return (
    <Flex vertical gap={24}>
      <Flex gap={10}>
        <SearchBar
          onSearch={(value) => setSearch(value)}
          placeholder={t(
            'organizational_structure.equipment_tab.search_placeholder'
          )}
        />

        <Select
          options={orderingOptions}
          onSelect={(opt) => setOrdering(opt)}
          placeholder={t('common.sort_by')}
        />
      </Flex>

      <Table
        bordered
        rowKey="id"
        loading={isLoading}
        columns={columns}
        scroll={{ x: 'max-content' }}
        dataSource={data?.data}
        pagination={{
          ...pagination,
          total: data?.totalCount
        }}
      />
    </Flex>
  )
}
